import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import SiteFooter from "../components/SiteFooter";
import useBrand from "../hooks/useBrand";

export default function Register() {
  const { register } = useAuth();
  const { brandName } = useBrand();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-screen max-h-[1080px] max-w-[1920px] mx-auto overflow-hidden flex flex-col justify-between"
      style={{ background: "#F6F5F2" }}
      data-testid="register-page"
    >
      <header className="px-6 md:px-12 lg:px-20 py-4 border-b border-black/5 flex items-center justify-between shrink-0">
        <Link to="/" className="font-display text-2xl tracking-tight" data-testid="register-brand">
          Car <span className="italic font-medium">Rental App</span>
          <span className="text-neutral-400 font-normal"> · {brandName}</span>
        </Link>
        <Link to="/login" className="text-sm link-underline" data-testid="register-nav-login">
          Sign in
        </Link>
      </header>

      <div className="flex-1 w-full px-6 md:px-12 lg:px-20 flex items-center overflow-hidden">
      <div className="grid lg:grid-cols-12 gap-12 items-center max-w-6xl mx-auto">
        <div className="lg:col-span-6 hidden lg:block order-2">
          <div className="overflow-hidden" style={{ aspectRatio: "4/5" }}>
            <img
              src="https://images.unsplash.com/photo-1601929862217-f1bf94503333?w=1200&q=85"
              alt="Car Rental App"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-6">
          <p className="overline mb-3">Begin your journey</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight">
            Join <em className="italic">Car Rental App</em>.
          </h1>
          <p className="mt-4 text-neutral-600 max-w-md">
            Create your account to book vehicles, manage reservations, and continue your
            journey with Car Rental App.
          </p>
          <p className="mt-2 text-sm text-neutral-600">
            Already with Car Rental App?{" "}
            <Link to="/login" className="link-underline font-semibold">Sign in</Link>
          </p>
          <form onSubmit={handleSubmit} className="mt-10 max-w-md space-y-6">
            <div>
              <label className="overline block mb-1">Full Name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="editorial-input w-full"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="overline block mb-1">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="editorial-input w-full"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="overline block mb-1">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input w-full"
                data-testid="register-password-input"
              />
              <p className="text-xs text-neutral-500 mt-2">Minimum 6 characters.</p>
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="register-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-4 hover:bg-neutral-800 transition-colors text-sm tracking-wide disabled:opacity-50"
              data-testid="register-submit-button"
            >
              {loading ? "Creating account…" : "Create account"}
            </button>
            <p className="text-sm text-neutral-600">
              Already a member?{" "}
              <Link to="/login" className="link-underline font-semibold">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
      </div>
      <SiteFooter />
    </div>
  );
}
